import { createContext, use, useCallback, useEffect, useState, type ReactNode } from "react";
import { onAuthStateChanged, type User } from "firebase/auth";
import { auth } from "./firebase";

/**
 * The 2020 code stored either the string "LOADING" or a User in one variable,
 * so every consumer had to write `currentUser === "LOADING"` guards and could
 * still forget one. A discriminated union makes the loading state impossible
 * to confuse with a signed-in user.
 */
export type AuthState =
  | { status: "loading" }
  | { status: "signedIn"; user: User }
  | { status: "signedOut" };

const AuthContext = createContext<AuthState>({ status: "loading" });
const RefreshContext = createContext<() => Promise<void>>(() => Promise.resolve());

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  useEffect(() => {
    // The 2020 version never unsubscribed, so every remount leaked a listener.
    return onAuthStateChanged(auth, (user) => {
      setState(user ? { status: "signedIn", user } : { status: "signedOut" });
    });
  }, []);

  /**
   * Re-read the user from Firebase.
   *
   * `emailVerified` comes off the cached ID token, and clicking the link in the
   * verification email happens in a *different tab* — nothing tells this one.
   * `reload()` refetches, but it mutates the existing `User` in place and fires
   * no auth-state listener, so without re-publishing here a just-verified user
   * would go on reading as unverified until a full reload.
   *
   * The new wrapper object is what makes React re-render: `user` is the same
   * mutated instance either way, so its identity cannot be the signal.
   */
  const refresh = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    await user.reload();
    // `reload()` refreshes the User record but **not** the ID token, and
    // Firestore rules read `email_verified` off the token — not off the record.
    // Without forcing a new one, a just-verified user still presents
    // `email_verified: false` to the database and every write is denied, while
    // the client cheerfully believes it is verified. Forcing it here is what
    // makes the two agree.
    await user.getIdToken(true);
    setState({ status: "signedIn", user: auth.currentUser ?? user });
  }, []);

  return (
    <AuthContext value={state}>
      <RefreshContext value={refresh}>{children}</RefreshContext>
    </AuthContext>
  );
}

export function useAuth(): AuthState {
  return use(AuthContext);
}

/** Re-read the signed-in user, picking up a verification done in another tab. */
export function useRefreshAuth(): () => Promise<void> {
  return use(RefreshContext);
}
